import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import {
  X, Mail, Phone, Calendar, Shield, Settings, LogOut,
  User, Camera, Lock, Save, Upload
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '@/services/api';
import { toast } from '@/components/ui/use-toast';
import useAuthStore from '@/store/authStore';
import { cn } from '@/lib/utils';

const Dialog = DialogPrimitive.Root;
const DialogPortal = DialogPrimitive.Portal;

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80 backdrop-blur-sm",
      className
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const UserProfileModal = ({ open, onOpenChange, user }) => {
  const navigate = useNavigate();
  const { logout, setUser } = useAuthStore();
  const [activeTab, setActiveTab] = useState('profile');
  const [isLoading, setIsLoading] = useState(false);

  // État pour les informations du profil
  const [profileData, setProfileData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    email: user?.email || '',
    phone_number: user?.phone_number || '',
  });

  // État pour le changement de mot de passe
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });

  // État pour la photo de profil
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);

  React.useEffect(() => {
    if (user) {
      setProfileData({
        first_name: user?.first_name || '',
        last_name: user?.last_name || '',
        email: user?.email || '',
        phone_number: user?.phone_number || '',
      });
    }
  }, [user]);

  const handleLogout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.log('Erreur lors de la déconnexion backend:', error);
    } finally {
      logout();
      toast({
        title: 'Déconnexion réussie',
        description: 'À bientôt !',
      });
      onOpenChange(false);
      navigate('/');
    }
  };

  const handleSettings = () => {
    onOpenChange(false);
    const role = user?.role?.toLowerCase();
    if (role === 'admin' || role === 'agent') {
      navigate('/admin/settings');
    } else if (role === 'client') {
      navigate('/client/settings');
    } else if (role === 'transporteur') {
      navigate('/transporter/settings');
    }
  };

  const handleProfileUpdate = async () => {
    setIsLoading(true);
    try {
      const response = await authAPI.updateProfile(profileData);
      setUser(response.data);

      toast({
        title: 'Profil mis à jour',
        description: 'Vos informations ont été sauvegardées avec succès.',
      });
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || 'Erreur lors de la mise à jour du profil.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast({
        title: 'Erreur',
        description: 'Les mots de passe ne correspondent pas.',
        variant: 'destructive',
      });
      return;
    }

    if (passwordData.new_password.length < 8) {
      toast({
        title: 'Erreur',
        description: 'Le mot de passe doit contenir au moins 8 caractères.',
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);
    try {
      await authAPI.changePassword({
        current_password: passwordData.current_password,
        new_password: passwordData.new_password,
      });

      toast({
        title: 'Mot de passe changé',
        description: 'Votre mot de passe a été modifié avec succès.',
      });

      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: '',
      });
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || 'Erreur lors du changement de mot de passe.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePhotoUpload = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: 'Erreur',
          description: 'La taille du fichier ne doit pas dépasser 5 MB.',
          variant: 'destructive',
        });
        return;
      }

      if (!file.type.startsWith('image/')) {
        toast({
          title: 'Erreur',
          description: 'Le fichier doit être une image.',
          variant: 'destructive',
        });
        return;
      }

      setProfilePhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePhotoSave = async () => {
    if (!profilePhoto) {
      toast({
        title: 'Erreur',
        description: 'Veuillez sélectionner une photo.',
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append('profile_photo', profilePhoto);

      const response = await authAPI.updateProfilePhoto(formData);
      setUser(response.data);

      // Mettre à jour la prévisualisation avec la nouvelle photo
      if (response.data.profile_photo) {
        setPhotoPreview(response.data.profile_photo);
      }

      toast({
        title: 'Photo mise à jour',
        description: 'Votre photo de profil a été mise à jour avec succès.',
      });

      setProfilePhoto(null);
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || 'Erreur lors de l\'upload de la photo.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getRoleLabel = (role) => {
    const roles = {
      ADMIN: 'Administrateur',
      AGENT: 'Agent',
      CLIENT: 'Client',
      TRANSPORTEUR: 'Transporteur',
    };
    return roles[role] || role;
  };

  const getInitials = () => {
    if (!user) return 'U';
    if (user?.first_name && user?.last_name) {
      const first = user.first_name[0] || '';
      const last = user.last_name[0] || '';
      return `${first}${last}`.toUpperCase();
    }
    if (user?.name) {
      const firstChar = user.name[0] || 'U';
      return String(firstChar).toUpperCase();
    }
    if (user?.email) {
      const firstChar = user.email[0] || 'U';
      return String(firstChar).toUpperCase();
    }
    return 'U';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <AnimatePresence>
        {open && (
          <DialogPortal>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <DialogOverlay />
            </motion.div>
            <motion.div
              initial={{ y: '-150%', x: '-50%', opacity: 0, scale: 0.95 }}
              animate={{ y: '-50%', x: '-50%', opacity: 1, scale: 1 }}
              exit={{ y: '-150%', x: '-50%', opacity: 0, scale: 0.95 }}
              transition={{
                type: 'spring',
                stiffness: 300,
                damping: 30,
                duration: 0.5,
              }}
              style={{
                position: 'fixed',
                left: '50%',
                top: '50%',
              }}
              className="z-50 w-full max-w-2xl max-h-[90vh] rounded-lg border border-border bg-card shadow-lg overflow-hidden flex flex-col"
              onPointerDownOutside={(e) => {
                e.preventDefault();
                onOpenChange(false);
              }}
            >
              {/* Header avec gradient */}
              <div className="relative bg-gradient-to-br from-primary via-primary/90 to-primary/80 p-6 pt-8 flex-shrink-0">
                <div className="absolute top-4 right-4">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 rounded-full bg-background/20 hover:bg-background/30 text-background"
                    onClick={() => onOpenChange(false)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>

                {/* Avatar et nom */}
                <div className="flex flex-col items-center space-y-4">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                    className="relative"
                  >
                    <Avatar className="w-24 h-24 border-4 border-background/30 shadow-xl">
                      <AvatarImage src={photoPreview || user?.profile_photo} />
                      <AvatarFallback className="bg-background/20 backdrop-blur-sm text-background text-4xl font-bold">
                        {getInitials()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="absolute -bottom-2 -right-2 w-8 h-8 rounded-full bg-green-500 border-4 border-background flex items-center justify-center">
                      <div className="w-3 h-3 rounded-full bg-background"></div>
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="text-center space-y-1"
                  >
                    <h3 className="text-2xl font-bold text-background">
                      {user?.first_name && user?.last_name
                        ? `${user.first_name} ${user.last_name}`
                        : user?.name || 'Utilisateur'}
                    </h3>
                    <p className="text-background/90 text-sm font-medium">
                      {getRoleLabel(user?.role)}
                    </p>
                    {user?.logiconnect_id && (
                      <p className="text-background/80 text-xs font-mono">
                        {user.logiconnect_id}
                      </p>
                    )}
                  </motion.div>
                </div>
              </div>

              {/* Contenu principal avec onglets */}
              <div className="flex-1 overflow-y-auto bg-card">
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                  <div className="px-6 pt-4 border-b border-border">
                    <TabsList className="grid w-full grid-cols-4">
                      <TabsTrigger value="profile" className="flex items-center gap-2">
                        <User className="h-4 w-4" />
                        <span className="hidden sm:inline">Profil</span>
                      </TabsTrigger>
                      <TabsTrigger value="photo" className="flex items-center gap-2">
                        <Camera className="h-4 w-4" />
                        <span className="hidden sm:inline">Photo</span>
                      </TabsTrigger>
                      <TabsTrigger value="security" className="flex items-center gap-2">
                        <Lock className="h-4 w-4" />
                        <span className="hidden sm:inline">Sécurité</span>
                      </TabsTrigger>
                      <TabsTrigger value="info" className="flex items-center gap-2">
                        <Shield className="h-4 w-4" />
                        <span className="hidden sm:inline">Infos</span>
                      </TabsTrigger>
                    </TabsList>
                  </div>

                  <div className="p-6">
                    {/* Onglet Profil */}
                    <TabsContent value="profile" className="space-y-4 mt-0">
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <label className="text-sm font-medium text-muted-foreground">
                              Prénom
                            </label>
                            <Input
                              value={profileData.first_name}
                              onChange={(e) => setProfileData({ ...profileData, first_name: e.target.value })}
                              placeholder="Votre prénom"
                            />
                          </div>
                          <div className="space-y-2">
                            <label className="text-sm font-medium text-muted-foreground">
                              Nom
                            </label>
                            <Input
                              value={profileData.last_name}
                              onChange={(e) => setProfileData({ ...profileData, last_name: e.target.value })}
                              placeholder="Votre nom"
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <label className="text-sm font-medium text-muted-foreground">
                            Email
                          </label>
                          <Input
                            type="email"
                            value={profileData.email}
                            onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                            placeholder="votre@email.com"
                          />
                        </div>

                        <div className="space-y-2">
                          <label className="text-sm font-medium text-muted-foreground">
                            Téléphone
                          </label>
                          <Input
                            type="tel"
                            value={profileData.phone_number}
                            onChange={(e) => setProfileData({ ...profileData, phone_number: e.target.value })}
                            placeholder="+224 627 613 835"
                          />
                        </div>

                        <Button
                          onClick={handleProfileUpdate}
                          disabled={isLoading}
                          className="w-full"
                        >
                          <Save className="h-4 w-4 mr-2" />
                          {isLoading ? 'Enregistrement...' : 'Enregistrer les modifications'}
                        </Button>
                      </div>
                    </TabsContent>

                    {/* Onglet Photo */}
                    <TabsContent value="photo" className="space-y-4 mt-0">
                      <div className="flex flex-col items-center space-y-6">
                        <div className="relative">
                          <Avatar className="w-32 h-32 border-4 border-primary shadow-xl">
                            <AvatarImage src={photoPreview || user?.profile_photo} />
                            <AvatarFallback className="bg-primary/20 text-primary text-5xl font-bold">
                              {getInitials()}
                            </AvatarFallback>
                          </Avatar>
                          {photoPreview && (
                            <div className="absolute inset-0 rounded-full bg-primary/20 flex items-center justify-center">
                              <Camera className="h-8 w-8 text-primary" />
                            </div>
                          )}
                        </div>

                        <div className="w-full space-y-4">
                          <div className="flex items-center justify-center w-full">
                            <label
                              htmlFor="photo-upload"
                              className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-border rounded-lg cursor-pointer hover:bg-accent/50 transition-colors"
                            >
                              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                <Upload className="h-8 w-8 mb-2 text-muted-foreground" />
                                <p className="mb-2 text-sm text-muted-foreground">
                                  <span className="font-semibold">Cliquez pour uploader</span> ou glissez-déposez
                                </p>
                                <p className="text-xs text-muted-foreground">PNG, JPG jusqu'à 5MB</p>
                              </div>
                              <input
                                id="photo-upload"
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={handlePhotoUpload}
                              />
                            </label>
                          </div>

                          {photoPreview && (
                            <Button
                              onClick={handlePhotoSave}
                              disabled={isLoading}
                              className="w-full"
                            >
                              <Save className="h-4 w-4 mr-2" />
                              {isLoading ? 'Enregistrement...' : 'Enregistrer la photo'}
                            </Button>
                          )}
                        </div>
                      </div>
                    </TabsContent>

                    {/* Onglet Sécurité */}
                    <TabsContent value="security" className="space-y-4 mt-0">
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <label className="text-sm font-medium text-muted-foreground">
                            Mot de passe actuel
                          </label>
                          <Input
                            type="password"
                            value={passwordData.current_password}
                            onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                            placeholder="Entrez votre mot de passe actuel"
                          />
                        </div>

                        <div className="space-y-2">
                          <label className="text-sm font-medium text-muted-foreground">
                            Nouveau mot de passe
                          </label>
                          <Input
                            type="password"
                            value={passwordData.new_password}
                            onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                            placeholder="Minimum 8 caractères"
                          />
                        </div>

                        <div className="space-y-2">
                          <label className="text-sm font-medium text-muted-foreground">
                            Confirmer le nouveau mot de passe
                          </label>
                          <Input
                            type="password"
                            value={passwordData.confirm_password}
                            onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                            placeholder="Confirmez votre nouveau mot de passe"
                          />
                        </div>

                        <Button
                          onClick={handlePasswordChange}
                          disabled={isLoading}
                          className="w-full"
                        >
                          <Lock className="h-4 w-4 mr-2" />
                          {isLoading ? 'Changement...' : 'Changer le mot de passe'}
                        </Button>
                      </div>
                    </TabsContent>

                    {/* Onglet Infos */}
                    <TabsContent value="info" className="space-y-4 mt-0">
                      <div className="space-y-3">
                        <div className="flex items-center space-x-3 p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors">
                          <div className="p-2 rounded-lg bg-primary/10">
                            <Mail className="h-4 w-4 text-primary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-muted-foreground">Email</p>
                            <p className="text-sm font-medium truncate">{user?.email || 'N/A'}</p>
                          </div>
                        </div>

                        {user?.phone_number && (
                          <div className="flex items-center space-x-3 p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors">
                            <div className="p-2 rounded-lg bg-primary/10">
                              <Phone className="h-4 w-4 text-primary" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-muted-foreground">Téléphone</p>
                              <p className="text-sm font-medium">{user.phone_number}</p>
                            </div>
                          </div>
                        )}

                        {user?.date_joined && (
                          <div className="flex items-center space-x-3 p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors">
                            <div className="p-2 rounded-lg bg-primary/10">
                              <Calendar className="h-4 w-4 text-primary" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-muted-foreground">Membre depuis</p>
                              <p className="text-sm font-medium">{formatDate(user.date_joined)}</p>
                            </div>
                          </div>
                        )}

                        <div className="flex items-center space-x-3 p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors">
                          <div className="p-2 rounded-lg bg-primary/10">
                            <Shield className="h-4 w-4 text-primary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-muted-foreground">Statut</p>
                            <p className="text-sm font-medium capitalize">
                              {user?.status === 'ACTIVE' ? 'Actif' :
                               user?.status === 'PENDING' ? 'En attente' :
                               user?.status === 'SUSPENDED' ? 'Suspendu' :
                               user?.status === 'BANNED' ? 'Banni' : user?.status || 'N/A'}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="pt-4 border-t border-border space-y-2">
                        <Button
                          variant="outline"
                          className="w-full justify-start"
                          onClick={handleSettings}
                        >
                          <Settings className="h-4 w-4 mr-2" />
                          Paramètres
                        </Button>
                        <Button
                          variant="destructive"
                          className="w-full justify-start"
                          onClick={handleLogout}
                        >
                          <LogOut className="h-4 w-4 mr-2" />
                          Déconnexion
                        </Button>
                      </div>
                    </TabsContent>
                  </div>
                </Tabs>
              </div>
            </motion.div>
          </DialogPortal>
        )}
      </AnimatePresence>
    </Dialog>
  );
};

export default UserProfileModal;
